import React, { useRef } from 'react';
import './VoiceModal.css';

// Modal voice UI (docs/mockups/ai-voice-modal-mockup.html). Purely presentational:
// state comes from the voiceModal reducer; every callback is a side-effect the
// dashboard owns. Dismissal per Apple HIG: swipe the sheet down (grabber signals
// it) or tap the scrim — no close button.

const MicIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M12 1a3 3 0 00-3 3v8a3 3 0 006 0V4a3 3 0 00-3-3z"/><path d="M19 10v2a7 7 0 01-14 0v-2"/><line x1="12" y1="19" x2="12" y2="23"/></svg>
);
const SpeakerIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5" fill="currentColor" stroke="none"/><path d="M15.5 8.5a5 5 0 010 7"/><path d="M18.5 5.5a9.5 9.5 0 010 13"/></svg>
);
const StopIcon = () => (
  <svg viewBox="0 0 24 24" fill="currentColor"><rect x="6" y="6" width="12" height="12" rx="2.5"/></svg>
);

function Control({ kind, icon, label, onClick, testid }) {
  return (
    <button type="button" className={`voice-ctl ${kind}`} data-testid={testid} onClick={onClick}>
      <span className="voice-ctl-disc">{icon}</span>
      <span className="voice-ctl-cap">{label}</span>
    </button>
  );
}

export default function VoiceModal({ state, liveTranscript, replySnippet, onStop, onSpeak, onCancel, onDismiss }) {
  const sheetRef = useRef(null);
  const dragRef = useRef({ startY: null, dy: 0 });

  if (!state || state === 'closed') return null;

  const onPointerDown = (e) => {
    if (e.target.closest('.voice-ctl')) return;
    dragRef.current = { startY: e.clientY, dy: 0 };
    sheetRef.current.classList.add('dragging');
    try { sheetRef.current.setPointerCapture(e.pointerId); } catch (_e) {}
  };
  const onPointerMove = (e) => {
    const d = dragRef.current;
    if (d.startY == null) return;
    d.dy = Math.max(0, e.clientY - d.startY);
    sheetRef.current.style.transform = `translateY(${d.dy}px)`;
  };
  const onPointerUp = () => {
    const d = dragRef.current;
    if (d.startY == null) return;
    sheetRef.current.classList.remove('dragging');
    if (d.dy > 110) {
      sheetRef.current.style.transform = 'translateY(110%)';
      setTimeout(() => { onDismiss(); }, 180);
    } else {
      sheetRef.current.style.transform = '';
    }
    dragRef.current = { startY: null, dy: 0 };
  };

  return (
    <div className="voice-scrim" data-testid="voice-modal" onClick={(e) => { if (e.target === e.currentTarget) onDismiss(); }}>
      <div
        ref={sheetRef}
        className="voice-sheet"
        data-voice-state={state}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
        onPointerCancel={onPointerUp}
      >
        <div className="voice-grab" />
        {state === 'listening' && (
          <div className="voice-state">
            <div className="voice-orb listening" data-testid="voice-orb-listening"><MicIcon /></div>
            <div className="voice-title">Listening…</div>
            <div className="voice-sub">{liveTranscript ? <span className="voice-live">“{liveTranscript}”</span> : 'Speak now — swipe down to leave'}</div>
          </div>
        )}
        {state === 'thinking' && (
          <div className="voice-state">
            <div className="voice-orb thinking"><span className="voice-tdots"><span/><span/><span/></span></div>
            <div className="voice-title">Thinking…</div>
            <div className="voice-sub">Getting your answer</div>
            <div className="voice-ctls">
              <Control kind="stop" icon={<StopIcon />} label="Cancel" onClick={onCancel} testid="voice-cancel" />
            </div>
          </div>
        )}
        {state === 'speaking' && (
          <div className="voice-state">
            <div className="voice-orb speaking" data-testid="voice-orb-speaking"><SpeakerIcon /></div>
            <div className="voice-title speaking-title">Speaking</div>
            <div className="voice-wave"><i/><i/><i/><i/><i/><i/><i/></div>
            <div className="voice-sub">{replySnippet ? `“${replySnippet}”` : ''}</div>
            <div className="voice-ctls">
              <Control kind="stop" icon={<StopIcon />} label="Stop" onClick={onStop} testid="voice-stop" />
              <Control kind="speak" icon={<MicIcon />} label="Speak" onClick={onSpeak} testid="voice-speak" />
            </div>
          </div>
        )}
        {state === 'paused' && (
          <div className="voice-state">
            <div className="voice-orb idle"><SpeakerIcon /></div>
            <div className="voice-title">Paused</div>
            <div className="voice-sub">Speak again, or swipe down to leave</div>
            <div className="voice-ctls">
              <Control kind="speak" icon={<MicIcon />} label="Speak" onClick={onSpeak} testid="voice-speak" />
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
